import { useEffect, useState } from "react";
import { Bell, BellOff, Check, Send, Smartphone } from "lucide-react";
import { api } from "./api";
import { Card } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { toast } from "sonner";

function applicationKey(value: string) {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(
    atob(base64 + "=".repeat((4 - (base64.length % 4)) % 4)),
    (c) => c.charCodeAt(0),
  );
}
async function registration() {
  const existing = await navigator.serviceWorker.getRegistration("/");
  if (existing?.active) return existing;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<never>((_, reject) =>
      setTimeout(
        () =>
          reject(
            Error(
              "O app está sendo atualizado. Feche e abra o Celeiro para tentar novamente.",
            ),
          ),
        10000,
      ),
    ),
  ]);
}
export function NotificationSettings() {
  const supported =
    typeof Notification !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window;
  const ios =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const installed =
    matchMedia("(display-mode: standalone)").matches ||
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
  const [permission, setPermission] = useState(
    supported ? Notification.permission : "default",
  );
  const [config, setConfig] = useState<{
    enabled: boolean;
    publicKey: string | null;
  } | null>(null);
  const [active, setActive] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true);
  const inspect = async () => {
    setLoading(true);
    setError("");
    try {
      setConfig(await api.pushConfig());
      if (supported) {
        setPermission(Notification.permission);
        const sw = await navigator.serviceWorker.getRegistration("/");
        const sub = await sw?.pushManager.getSubscription();
        setActive(
          !!sub &&
            Notification.permission === "granted" &&
            (await api.pushStatus(sub.endpoint)).active,
        );
      }
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Não foi possível verificar as notificações.",
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void inspect();
  }, []);
  const enable = async () => {
    setBusy(true);
    setError("");
    try {
      // Must be the first awaited operation, directly from the user's tap on iOS.
      const permission = await Notification.requestPermission();
      setPermission(permission);
      if (permission !== "granted") return;
      const sw = await registration();
      const sub =
        (await sw.pushManager.getSubscription()) ||
        (await sw.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationKey(config!.publicKey!),
        }));
      await api.pushSubscribe(sub.toJSON());
      setActive(true);
      await api.pushTest(sub.endpoint);
      toast.success("Notificações ativadas. Enviamos um aviso de teste!");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível ativar.");
    } finally {
      setBusy(false);
    }
  };
  const disable = async () => {
    setBusy(true);
    setError("");
    try {
      const sw = await registration();
      const sub = await sw.pushManager.getSubscription();
      if (sub) {
        await api.pushUnsubscribe(sub.endpoint);
        await sub.unsubscribe();
      }
      setActive(false);
      toast.success("Notificações desativadas neste dispositivo");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível desativar.");
    } finally {
      setBusy(false);
    }
  };
  const test = async () => {
    setBusy(true);
    setError("");
    try {
      const sub = await (await registration()).pushManager.getSubscription();
      if (!sub) throw Error("Ative as notificações neste dispositivo.");
      await api.pushTest(sub.endpoint);
      toast.success("Notificação de teste enviada");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível testar.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="module-card notification-settings">
      <div className="settings-card-head">
        <div>
          <span className="card-kicker">LEMBRETES</span>
          <h3>Suas contas, no tempo certo</h3>
          <p>
            Receba um aviso um dia antes e no dia do vencimento, mesmo com o app
            fechado.
          </p>
        </div>
        <Badge variant="secondary">
          {active ? (
            <>
              <Check size={12} /> Ativadas
            </>
          ) : (
            <>
              <BellOff size={12} /> Desativadas
            </>
          )}
        </Badge>
      </div>
      <div className="notification-schedule">
        <Bell />
        <div>
          <strong>Contas fixas e faturas</strong>
          <span>
            Às 9h · horário de Brasília · somente o que ainda está pendente
          </span>
        </div>
      </div>
      {ios && !installed ? (
        <p className="form-notice">
          <Smartphone size={18} /> No iPhone, abra o Celeiro no Safari, toque em
          Compartilhar → Adicionar à Tela de Início. Depois abra pelo ícone e
          ative as notificações aqui. Requer iOS 16.4 ou mais recente.
        </p>
      ) : !supported ? (
        <p className="form-notice">
          Este navegador não oferece notificações push. Use uma versão atual do
          Safari, Chrome, Edge ou Firefox.
        </p>
      ) : permission === "denied" ? (
        <p className="form-notice">
          As notificações estão bloqueadas. Nos Ajustes do iPhone, abra
          Notificações → Celeiro e permita os avisos. Em outros dispositivos,
          revise a permissão do site no navegador e tente novamente.
        </p>
      ) : null}
      {config && !config.enabled && (
        <p className="form-notice">
          O serviço de notificações está indisponível no momento. Tente
          novamente mais tarde.
        </p>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div className="notification-settings-actions">
        {active ? (
          <>
            <Button variant="outline" disabled={busy} onClick={test}>
              <Send /> Enviar teste
            </Button>
            <Button variant="ghost" disabled={busy} onClick={disable}>
              Desativar neste dispositivo
            </Button>
          </>
        ) : (
          <Button
            variant="liquid"
            disabled={
              loading ||
              busy ||
              !supported ||
              (ios && !installed) ||
              permission === "denied" ||
              !config?.enabled
            }
            onClick={enable}
          >
            <Bell />
            {busy ? "Ativando…" : "Ativar notificações"}
          </Button>
        )}
        <Button variant="ghost" disabled={busy || loading} onClick={inspect}>
          {loading ? "Verificando…" : "Verificar novamente"}
        </Button>
      </div>
      <small>
        A permissão vale para este dispositivo. Contas pagas deixam de gerar
        novos avisos; o sistema do celular pode adiar a entrega se estiver
        offline ou em modo Foco.
      </small>
    </Card>
  );
}
